import { Avatar } from "@fullscript/aviary-web";
import { useTranslation } from "react-i18next";

import { useSearchParams } from "@shared/react-router-dom/react-router-dom";
import type { PatientAttributes } from "@unauthenticated/shared/data/types/PatientAttributes.d";
import { PatientStoreSignUpForm } from "@unauthenticated/signupSignin/components/PatientStoreSignUpForm/PatientStoreSignUpForm";
import { SignUpHeader } from "@unauthenticated/signupSignin/components/SignUpHeader/SignUpHeader";
import { SignUpSubtitle } from "@unauthenticated/signupSignin/components/SignUpSubtitle/SignUpSubtitle";
import { SignUpTitle } from "@unauthenticated/signupSignin/components/SignUpTitle/SignUpTitle";
import { StoreLandingLayout } from "@unauthenticated/signupSignin/components/StoreLandingLayout/StoreLandingLayout";
import { l } from "@unauthenticated/signupSignin/locales/i18n";

import * as styles from "./PatientConnectSignUpPage.styles";

interface Props {
  patientAttributes: PatientAttributes;
  additionalAttributes?: any;
  pageVersion?: string;
  storeId?: string;
  isPreview?: boolean;
  isStoreOwner: boolean;
  ssoErrors?: string;
  activePatientPromotion?: boolean;
}

const PatientConnectSignUpPage = ({
  patientAttributes,
  additionalAttributes,
  pageVersion,
  isPreview = false,
  isStoreOwner,
  storeId,
  ssoErrors,
  activePatientPromotion,
}: Props) => {
  const { t } = useTranslation();
  const { organizationLogo, storeName, pracName, pracAvatar, iconLogo, storeSlug } =
    patientAttributes;
  const [searchParameters] = useSearchParams();
  const requestedPractitionerId = searchParameters.get("requestedPractitionerId") ?? undefined;

  return (
    <StoreLandingLayout
      storeName={storeName}
      storeId={storeId}
      storeSlug={storeSlug}
      pracAvatar={pracAvatar}
      isPreview={isPreview}
      isStoreOwner={isStoreOwner}
      isActivePromotion={activePatientPromotion}
      organizationLogo={organizationLogo}
      iconLogo={iconLogo}
    >
      <SignUpHeader>
        {!!pracAvatar && (
          <div css={styles.avatarWrapper}>
            <Avatar
              imageUrl={pracAvatar}
              size="large"
              alt={t(l.connect.pracAvatarAlt, { pracName })}
            />
          </div>
        )}
        <SignUpTitle title={pracName} />
        <SignUpSubtitle
          subtitle={t(l.connect.subtitle, {
            pracName,
            interpolation: { escapeValue: false },
          })}
        />
      </SignUpHeader>
      <PatientStoreSignUpForm
        storeId={storeId}
        pageVersion={pageVersion}
        patientAttributes={patientAttributes}
        additionalAttributes={additionalAttributes}
        isPreview={isPreview}
        ssoErrors={ssoErrors}
        requestedPractitionerId={requestedPractitionerId}
      />
    </StoreLandingLayout>
  );
};

export { PatientConnectSignUpPage };
